'use client';

import { useSyncExternalStore } from 'react';

// Favorites and the compare tray are per-device lists kept in localStorage,
// read and written from eight different components. Each used to parse and
// serialize the comma-separated string on its own, which is exactly how the
// favorites-merge bug (batch A, item 7) happened: two copies of the same
// logic quietly drifting apart. These are the single source of truth for
// that parsing/serialization; components keep their own state and effects,
// they just stop reimplementing "how do we read/write the list".

export const FAVORITES_KEY = 'rec_favorites';
export const COMPARE_KEY = 'rec_compare_ids';
// Broadcast after every write so every mounted component (navbar, bottom nav,
// floating compare bar, card hearts, ...) re-reads and stays in sync.
export const LOCAL_LIST_EVENT = 'storage-update';

function readIds(key: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    return (localStorage.getItem(key) || '').split(',').filter(Boolean);
  } catch {
    return [];
  }
}

function writeIds(key: string, ids: string[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, ids.join(','));
    window.dispatchEvent(new Event(LOCAL_LIST_EVENT));
  } catch {
    // Private browsing / storage disabled: the feature just does nothing.
  }
}

export const getFavoriteIds = () => readIds(FAVORITES_KEY);
export const getCompareIds = () => readIds(COMPARE_KEY);
export const setFavoriteIds = (ids: string[]) => writeIds(FAVORITES_KEY, ids);
export const setCompareIds = (ids: string[]) => writeIds(COMPARE_KEY, ids);

// Returns the new state (true = now favorited) so callers can update their
// own UI immediately without a second read.
export function toggleFavorite(id: string): boolean {
  const ids = getFavoriteIds();
  const idx = ids.indexOf(id);
  if (idx >= 0) {
    ids.splice(idx, 1);
    setFavoriteIds(ids);
    return false;
  }
  ids.push(id);
  setFavoriteIds(ids);
  return true;
}

// Four is what fits side by side, including on a phone, so it is the cap
// everywhere: the cards, the listing page and the compare table itself.
export const MAX_COMPARE = 4;

// Returns 'added' | 'removed' | 'max' (the compare tray caps at maxItems).
export function toggleCompare(id: string, maxItems = MAX_COMPARE): 'added' | 'removed' | 'max' {
  const ids = getCompareIds();
  const idx = ids.indexOf(id);
  if (idx >= 0) {
    ids.splice(idx, 1);
    setCompareIds(ids);
    return 'removed';
  }
  if (ids.length >= maxItems) return 'max';
  ids.push(id);
  setCompareIds(ids);
  return 'added';
}

export function removeFromCompare(id: string) {
  setCompareIds(getCompareIds().filter((x) => x !== id));
}

export function clearCompare() {
  setCompareIds([]);
}

// ---------------------------------------------------------------------------
// Reading these lists from React.
//
// Item 61. Every component that showed a favourite heart or a compare count did
// the same thing: useState, then useEffect to read localStorage on mount, then
// a listener to re-read on the broadcast. Thirty of those, flagged by
// react-hooks/set-state-in-effect.
//
// The pattern is not wrong - the server cannot know what is in localStorage,
// and reading it during render is a hydration mismatch - but it is a hand-built
// version of something React has: useSyncExternalStore. That gives the same
// safety with none of the effects, and it fixes a real defect the hand-built
// version had, which is that each component re-read storage separately and they
// could briefly disagree.
//
// getSnapshot has to return the same reference when nothing changed, or React
// re-renders forever. readIds allocates a new array every call, so the raw
// string is cached and the parsed array reused while it is unchanged.

const snapshotCache = new Map<string, { raw: string; ids: string[] }>();
const EMPTY: string[] = [];

function cachedIds(key: string): string[] {
  if (typeof window === 'undefined') return EMPTY;
  let raw = '';
  try {
    raw = localStorage.getItem(key) || '';
  } catch {
    return EMPTY;
  }
  const cached = snapshotCache.get(key);
  if (cached && cached.raw === raw) return cached.ids;
  const ids = raw.split(',').filter(Boolean);
  snapshotCache.set(key, { raw, ids });
  return ids;
}

/** The snapshot React reads. Exported so a test can hold it to its contract. */
export const getFavoriteSnapshot = (): string[] => cachedIds(FAVORITES_KEY);
export const getCompareSnapshot = (): string[] => cachedIds(COMPARE_KEY);

/** What the server renders: empty, so the first client render matches it. */
export const getServerSnapshot = (): string[] => EMPTY;

export function subscribeToLists(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(LOCAL_LIST_EVENT, onChange);
  // 'storage' fires in the other tabs, which is how two open tabs stay in step.
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(LOCAL_LIST_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** The favourite ids, kept in step with every other component and every tab. */
export function useFavoriteIds(): string[] {
  // The server has no localStorage, so it renders the empty list and the first
  // client render matches it. Anything else is a hydration mismatch.
  return useSyncExternalStore(subscribeToLists, getFavoriteSnapshot, getServerSnapshot);
}

/** The compare ids, same guarantees. */
export function useCompareIds(): string[] {
  return useSyncExternalStore(subscribeToLists, getCompareSnapshot, getServerSnapshot);
}

export function useIsFavorited(id: string): boolean {
  return useFavoriteIds().includes(id);
}

export function useIsCompared(id: string): boolean {
  return useCompareIds().includes(id);
}
